import "../styles/globals.scss";

import { ConfigProvider, FloatButton } from "antd";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";

import AppLayout from "@containers/app-layout";
import type { AppProps } from "next/app";
import { ArrowUpOutlined } from "@ant-design/icons";
import Cookies from "js-cookie";
import DocumentTitle from "@components/document-title";
import { ErrorBoundary } from "@sentry/react";
import { Locale } from "antd/lib/locale-provider";
import { NextAdapter } from "next-query-params";
import Ops from "@components/ops";
import PrivateRoute from "@guards/private-route";
import { Provider } from "react-redux";
import PublicRoute from "@guards/public-route";
import { QueryParamProvider } from "use-query-params";
import { appWithTranslation } from "next-i18next";
import { blue } from "@ant-design/colors";
import dayjs from "dayjs";
import en from "antd/lib/locale/en_US";
import it from "dayjs/locale/it";
import itIT from "antd/lib/locale/it_IT";
import relativeTime from "dayjs/plugin/relativeTime";
import tolerateExternalDomChanges from "@helpers/tolerate-external-dom-changes";
import { store } from "@store";
import { useIsClient } from "usehooks-ts";
import { useRouter } from "next/router";

tolerateExternalDomChanges();

dayjs.extend(relativeTime);

const backtopStyle: React.CSSProperties = {
  height: 32,
  width: 32,
  lineHeight: "32px",
  borderRadius: 4,
  backgroundColor: blue.primary,
  color: "#fff",
  textAlign: "center",
  fontSize: 16,
};

function MyApp({ Component, pageProps }: AppProps) {
  const isClient = useIsClient();
  const { pathname, locale: language } = useRouter();
  const [locale, setLocale] = useState<Locale>(
    language === "it" ? itIT : en
  );

  const queryClient = new QueryClient();

  useEffect(() => {
    Cookies.set("NEXT_LOCALE", language, {
      path: "/",
      secure: process.env.NODE_ENV === "production",
      expires: dayjs().add(1, "year").toDate(),
    });

    setLocale(language === "it" ? itIT : en);

    dayjs.locale(language === "it" ? it : "en");
  }, [language]);

  if (!isClient) {
    return null;
  }

  return (
    <>
      <DocumentTitle />

      {pathname === "/" ? (
        <PublicRoute>
          <Component {...pageProps} />
        </PublicRoute>
      ) : pathname === "/unauthorized" ? (
        <ConfigProvider
          locale={locale}
          prefixCls="opi"
          iconPrefixCls="opiicon"
          theme={{
            token: {
              fontFamily: "Muli",
              colorPrimary: process.env.NEXT_PUBLIC_PRIMARY_COLOR,
            },
          }}
        >
          <Component {...pageProps} />
        </ConfigProvider>
      ) : (
        <Provider store={store}>
          <ConfigProvider
            locale={locale}
            prefixCls="opi"
            iconPrefixCls="opiicon"
            theme={{
              token: {
                fontFamily: "Muli",
                colorPrimary: process.env.NEXT_PUBLIC_PRIMARY_COLOR,
              },
            }}
          >
            <QueryParamProvider
              adapter={NextAdapter}
              options={{ removeDefaultsFromUrl: true }}
            >
              <ErrorBoundary fallback={() => <Ops />}>
                <PrivateRoute>
                  <QueryClientProvider client={queryClient}>
                    <AppLayout>
                      <Component {...pageProps} />

                      <FloatButton.BackTop duration={0}>
                        <div style={backtopStyle}>
                          <ArrowUpOutlined />
                        </div>
                      </FloatButton.BackTop>
                    </AppLayout>
                  </QueryClientProvider>
                </PrivateRoute>
              </ErrorBoundary>
            </QueryParamProvider>
          </ConfigProvider>
        </Provider>
      )}
    </>
  );
}

export default appWithTranslation(MyApp);
